import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { authResponseToSessionData } from './auth/utils';

import { sessionMiddleware } from '@/middleware/session';
import type { TLAILoginResponse } from '@/types/api';
import { apiClient } from '@/utils/api-client';
import { createFormData, extractHTTPError } from '@/utils/http-client';
import { safeInvoke } from '@/utils/safe-invoke';
import { updateSessionDataHeaders } from '@/utils/session';

const CallbackSchema = z.object({
  code: z.string().min(1),
  redirect_uri: z.string().min(1),
  state: z.string().min(1),
});

export const callbackServerFn = createServerFn({ method: 'POST' })
  .inputValidator(CallbackSchema)
  .middleware([sessionMiddleware])
  .handler(async ({ data, context: { session } }) => {
    const apiClientInstance = await apiClient({ authenticated: false });

    const response = await safeInvoke(
      apiClientInstance.post<TLAILoginResponse>(
        '/auth/sso/callback',
        createFormData({
          code: data.code,
          redirect_uri: data.redirect_uri,
          state: data.state,
        }),
      ),
    );

    if (response.error) {
      console.error(
        '[CALLBACK] Error processing callback:',
        extractHTTPError(response.error, 'An unexpected error occurred'),
      );
      throw response.error;
    }

    if (response.data) {
      return new Response(JSON.stringify({ success: true }), {
        headers: updateSessionDataHeaders(new Headers(), {
          ...session,
          ...authResponseToSessionData(response.data),
        }),
      });
    }

    return new Response('No data returned from callback endpoint', { status: 500 });
  });
